import { useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { CapDocumentNotice } from './cap-document-notice'

/**
 * El visor de una imagen del cartapacio.
 * Réplica de `apps/mobile/src/features/documentos/ui/image-viewer.tsx`.
 *
 * ## Lo que se replica: que el borde redondo abrace la FOTO, no el marco
 *
 * Es la decisión que separa este visor del `CapImage` que había antes. `CapImage` dimensiona un
 * CONTENEDOR y mete la foto adentro con `object-contain`, así que sobre un plano apaisado el
 * recuadro redondeado quedaba dibujado alrededor de dos bandas vacías. La app resuelve eso midiendo
 * la relación de aspecto real en `onLoad` y encogiendo la caja hasta la forma de la foto
 * (`containBox`).
 *
 * En la web esa aritmética no hace falta: un `<img>` con `max-width`/`max-height` al 100 % y
 * `object-contain` **ya es** su propia caja contenida, así que el radio y el recorte se aplican
 * sobre el elemento que tiene exactamente la forma de la foto. Mismo resultado, sin retipear una
 * regla que el paquete no necesita publicar.
 *
 * ## Lo que NO se replica: el pellizco, el arrastre y el doble toque
 *
 * Son GESTOS, y el ADR 0039 los deja explícitamente afuera («fidelidad de contenido y composición
 * —medidas, tipografía, colores, radios, skeletons, copy exacto—, no de chrome»). Replicarlos
 * pediría `react-native-gesture-handler` en la web y un zoom que nadie va a usar con un mouse para
 * decidir si un documento está bien publicado.
 *
 * ## Los dos fallos dicen cosas distintas
 *
 * Que la FIRMA no llegue y que el archivo no se pueda pintar son dos cosas, y la app las separa con
 * dos copys. Un solo mensaje mandaría a buscar el problema al lado equivocado la mitad de las
 * veces.
 */
export type CapImageViewerProps = {
  /** La URL firmada. Ausente mientras la firma se pide. */
  url: string | null
  /** La firma todavía viaja. */
  isPending: boolean
  /** La firma falló: no hay nada que pintar y hay que decir por qué. */
  isError?: boolean
  className?: string
}

type CapImageViewerState = 'loading' | 'loaded' | 'failed'

export function CapImageViewer({
  url,
  isPending,
  isError = false,
  className
}: CapImageViewerProps) {
  /*
    No hace falta volver a 'loading' al cambiar de `url`: la ficha se monta con `key={documentId}`
    (`client-app-preview.tsx`), así que ir de un documento a otro trae una instancia nueva. Un
    efecto que lo reseteara igual sería código que nunca corre, y el día que alguien sacara la
    `key` lo taparía en vez de dejarlo fallar.
  */
  const [state, setState] = useState<CapImageViewerState>('loading')

  if (isError) {
    return (
      <CapDocumentNotice
        title="No pudimos abrir la imagen"
        detail="Vuelve a intentarlo en unos segundos."
        className={className}
      />
    )
  }

  if (state === 'failed') {
    return (
      <CapDocumentNotice
        title="No pudimos mostrar la imagen"
        detail="El archivo puede estar dañado o el enlace ya venció."
        className={className}
      />
    )
  }

  return (
    <div className={cn('relative flex h-full w-full items-center justify-center', className)}>
      {isPending || !url ? (
        <CapActivityIndicator />
      ) : (
        <>
          {state === 'loading' ? <CapActivityIndicator className="absolute" /> : null}
          {/*
            El `transition` de 200 ms de `expo-image`: la foto ENTRA, no aparece de golpe sobre el
            indicador. Sin esto el cambio de un elemento por otro se lee como un parpadeo.
          */}
          <img
            src={url}
            alt="Vista previa del documento"
            className={cn(
              'max-h-full max-w-full rounded-[var(--cap-radius-lg)] object-contain transition-opacity duration-200',
              state === 'loaded' ? 'opacity-100' : 'opacity-0'
            )}
            onLoad={() => setState('loaded')}
            onError={() => setState('failed')}
          />
        </>
      )}
    </div>
  )
}

/** El `ActivityIndicator` de la app: un anillo girando en el color de marca. */
function CapActivityIndicator({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Cargando"
      className={cn('h-6 w-6 animate-spin rounded-[var(--cap-radius-full)] border-2', className)}
      style={{
        borderColor: 'var(--cap-neutral-200)',
        borderTopColor: 'var(--cap-brand-700)'
      }}
    />
  )
}
